import { useReducer } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useCarrierSyncHealth } from "@/lib/api";

// Dismiss state lives in sessionStorage so it survives a page refresh but
// resets on a new tab/session. It is keyed to the exact set of down carriers,
// so if that set changes (one recovers, or another goes down) the banner
// resurfaces with the current carriers (CSR-1343).
const dismissKey = (jobTypes: string[]) =>
  `carrierSyncDismissed:${[...jobTypes].sort().join(",")}`;

export function CarrierSyncBanner() {
  const { data } = useCarrierSyncHealth();
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0);

  const down = (data ?? [])
    .filter((carrier) => carrier.show)
    .sort((a, b) => a.jobType.localeCompare(b.jobType));

  if (down.length === 0) return null;

  const key = dismissKey(down.map((carrier) => carrier.jobType));
  if (sessionStorage.getItem(key) === "1") return null;

  const dismiss = () => {
    sessionStorage.setItem(key, "1");
    forceRender();
  };

  return (
    <div className="shrink-0">
      <div
        role="alert"
        className="flex items-center gap-2 border-l-4 px-4 py-2 text-sm"
        style={{
          backgroundColor: "#F9E0A5",
          borderColor: "#F1B72C",
          color: "#1F2937",
        }}
      >
        <AlertTriangle
          className="h-4 w-4 shrink-0"
          style={{ color: "#F1B72C" }}
          aria-hidden="true"
        />
        <span className="flex-1">
          {down.map((carrier, index) => (
            <span key={carrier.jobType}>
              {index > 0 ? (index === down.length - 1 ? " and " : ", ") : ""}
              <span className="font-bold">{carrier.carrier}</span>
            </span>
          ))}{" "}
          {down.length > 1 ? "syncs are" : "sync is"} currently unstable. Some
          updates may require manual action. Our team is working on a fix.
        </span>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss carrier sync warning"
          className="shrink-0 cursor-pointer"
          style={{ color: "#9CA3AF" }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
